import React from 'react'
import { connect } from 'react-redux'
import styles from './Feed.scss'
import { Card } from 'common/styleguide/Card'
import { ColWrap, MainCol, SecondaryCol } from 'common/layouts/SideNavLayout/TwoCol'
import { IntroWidget } from 'common/IntroWidget'
import { Loadable } from 'common/Loadable'
import { FeedActivity } from './FeedActivity'
import { FollowsCard } from './FollowsCard'
import { CTA } from 'common/styleguide/CTA'
import { InviteFriendsDialog, INVITE_FRIENDS_DIALOG } from './dialogs/InviteFriendsDialog'
import _ from 'lodash'
import { apiv3 } from 'lib/apiv3'
import * as currentUserActions from 'lib/api/currentUser'
import { followActions, followSelectors } from 'data/resources/follows'
import { DEFAULT_OFFSET, DEFAULT_PER_PAGE } from 'lib/loadAllPages'
import { Msg, featureNames, commonVerbs } from 'lib/localization'
import Helmet from 'react-helmet'
import { IeNudgeBar } from 'common/IeNudgeBar'
import { FormattedMessage as T, injectIntl, defineMessages } from 'react-intl'

export const friendsMessages = defineMessages({
  addFriends: {
    id: 'friends.addFriends',
    defaultMessage: 'Add Friends'
  },
  findFriends: {
    id: 'feed.findFriends',
    defaultMessage: 'Find Friends'
  },
  inviteFriends: {
    id: 'feed.inviteFriends',
    defaultMessage: 'Invite Friends'
  }
})

export class Feed extends React.PureComponent {
  constructor (props) {
    super(props)
    const { loadableResources: { feed } } = this.props

    this.state = {
      dialog: null,
      feed,
      loadingMore: false,
      offset: DEFAULT_OFFSET
    }
  }

  showDialog = () => {
    this.setState({ dialog: INVITE_FRIENDS_DIALOG })
  }

  hideDialog = () => {
    this.setState({ dialog: null })
  }

  getMoreFriendsActivity = () => {
    const { currentUser } = this.props
    const { offset } = this.state

    apiv3.users.feedItems(currentUser.id, {
      extras: true,
      order_by: 'created_at',
      order_dir: 'desc',
      page: offset / DEFAULT_PER_PAGE,
      perPage: DEFAULT_PER_PAGE
    }).then((response) => {
      this.setState((prevState) => {
        const combinedResults = _.concat(prevState.feed.results, response.results)
        const combinedExtras = _.concat(prevState.feed.extras, response.extras)
        return {
          feed: Object.assign(
            {},
            prevState.feed,
            { results: combinedResults, extras: combinedExtras, totalCount: response.totalCount }
          ),
          loadingMore: false
        }
      })
    })
  }

  loadMore = () => {
    this.setState((state) => ({ loadingMore: true, offset: state.offset + DEFAULT_PER_PAGE }), () => this.getMoreFriendsActivity())
  }

  render () {
    const {
      followers,
      following,
      currentUser,
      setPreference,
      intl
    } = this.props
    const { feed, dialog, loadingMore, offset } = this.state

    const followCardProps = {
      user: currentUser,
      currentUser,
      interactive: true,
      friendBadge: true,
      followers,
      following,
      defaultTo: 'following'
    }

    return (
      <div className={styles.Feed}>
        <Helmet title={`${intl.formatMessage(featureNames.feed)} · Ride with GPS`} />
        <IeNudgeBar currentUser={currentUser} setPreference={setPreference} />
        <ColWrap>
          {dialog === INVITE_FRIENDS_DIALOG && <InviteFriendsDialog intl={intl} onRequestClose={this.hideDialog} />}
          <MainCol>
            <h2 data-heading-alt><Msg m={featureNames.feed} /></h2>
            <Card noPadding>
              {feed && feed.results.length === 0
                ? <UserHasNoFeedActivity />
                : <FeedActivity
                  feed={feed}
                  currentUser={currentUser}
                  loadMore={this.loadMore}
                  loadingMore={loadingMore}
                  offset={offset} />}
            </Card>
          </MainCol>
          <SecondaryCol>
            <h2 data-heading-alt><Msg m={commonVerbs.follow} /></h2>
            <Card>
              <CTA secondary margins={false} href='/users'><Msg m={friendsMessages.findFriends} /></CTA>
              <CTA link margins={false} onClick={this.showDialog} className={styles.findFriendsCTA}>
                <Msg m={friendsMessages.inviteFriends} />
              </CTA>
            </Card>
            {currentUser.visibility !== 1 && <FollowsCard {...followCardProps} />}
          </SecondaryCol>
        </ColWrap>
      </div>
    )
  }
}

const UserHasNoFeedActivity = () => (
  <IntroWidget large i='md-person-add' link={<a href='/users'><Msg m={friendsMessages.addFriends} /></a>}>
    <T id='friends.addFriendsActivity' defaultMessage='Add friends to see their activity here' />
  </IntroWidget>
)

const loadInitialResources = _.memoize(({ currentUser, loadFollows }) => {
  const loadCurrentUserFollows = () => (
    loadFollows({ userId: currentUser.id, extras: 'users' })
  )

  const loadCurrentUserInitialFeed = () => (
    apiv3.users.feedItems(currentUser.id, { extras: true, order_by: 'created_at', order_dir: 'desc' })
  )

  const loadAll = Promise.all([
    loadCurrentUserFollows(),
    loadCurrentUserInitialFeed()
  ])

  return loadAll.then(
    ([_follows, feed]) => ({
      feed
    })
  )
})

const LoadableFeed = (props) => {
  return <Loadable
    {...props}
    component={Feed}
    loadingText={<T id='feed.loading' defaultMessage='Loading Feed' />}
    resourceLoaded={() => loadInitialResources(props)} />
}

const mapDispatchToProps = {
  loadFollows: followActions.list,
  setPreference: currentUserActions.setPreference
}

const mapStateToProps = state => {
  const { currentUser } = state

  return {
    currentUser,
    following: followSelectors.followsForUser(state, currentUser.id),
    followers: followSelectors.byItem(state, currentUser.id, 'User')
  }
}

export const FeedContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(LoadableFeed))
